import { useEffect } from 'react'
import { Link } from 'react-router-dom'

import goldGmtCream480Avif from '../assets/home-watches/gold-gmt-cream-480.avif'
import goldGmtCream480Webp from '../assets/home-watches/gold-gmt-cream-480.webp'
import goldGmtCream768Avif from '../assets/home-watches/gold-gmt-cream-768.avif'
import goldGmtCream768Webp from '../assets/home-watches/gold-gmt-cream-768.webp'
import skxGreenTerra360Avif from '../assets/home-watches/skx-green-terra-360.avif'
import skxGreenTerra360Webp from '../assets/home-watches/skx-green-terra-360.webp'
import skxGreenTerra640Avif from '../assets/home-watches/skx-green-terra-640.avif'
import skxGreenTerra640Webp from '../assets/home-watches/skx-green-terra-640.webp'
import goldOpenHeart360Avif from '../assets/home-watches/gold-open-heart-360.avif'
import goldOpenHeart360Webp from '../assets/home-watches/gold-open-heart-360.webp'
import goldOpenHeart640Avif from '../assets/home-watches/gold-open-heart-640.avif'
import goldOpenHeart640Webp from '../assets/home-watches/gold-open-heart-640.webp'
import explorerTropic360Avif from '../assets/home-watches/explorer-tropic-360.avif'
import explorerTropic360Webp from '../assets/home-watches/explorer-tropic-360.webp'
import explorerTropic640Avif from '../assets/home-watches/explorer-tropic-640.avif'
import explorerTropic640Webp from '../assets/home-watches/explorer-tropic-640.webp'
import './home.css'

type WatchBuild = {
  name: string
  parts: string
  alt: string
  width: number
  height: number
  avif: { small: string; large: string; smallWidth: number; largeWidth: number }
  webp: { small: string; large: string; smallWidth: number; largeWidth: number }
}

const WATCH_BUILDS: WatchBuild[] = [
  {
    name: 'Gold GMT cream',
    parts: '1908 gold 36 · Cream GMT dial · Black pebbled leather',
    alt: 'Horatix watch with a cream GMT dial, gold 1908 case, and black pebbled leather strap',
    width: 768,
    height: 1322,
    avif: {
      small: goldGmtCream480Avif,
      large: goldGmtCream768Avif,
      smallWidth: 480,
      largeWidth: 768,
    },
    webp: {
      small: goldGmtCream480Webp,
      large: goldGmtCream768Webp,
      smallWidth: 480,
      largeWidth: 768,
    },
  },
  {
    name: 'Forest green diver',
    parts: 'SKX013 38 green · Terra explorer dial · Green cordovan',
    alt: 'Horatix dive watch with a white explorer dial, green SKX013 case, and green cordovan strap',
    width: 427,
    height: 640,
    avif: {
      small: skxGreenTerra360Avif,
      large: skxGreenTerra640Avif,
      smallWidth: 240,
      largeWidth: 427,
    },
    webp: {
      small: skxGreenTerra360Webp,
      large: skxGreenTerra640Webp,
      smallWidth: 240,
      largeWidth: 427,
    },
  },
  {
    name: 'Gold open-heart',
    parts: '1908 gold 36 · Hemicube dial · Black cordovan',
    alt: 'Horatix watch with a gold 1908 case, geometric open-heart dial, and black cordovan strap',
    width: 427,
    height: 640,
    avif: {
      small: goldOpenHeart360Avif,
      large: goldOpenHeart640Avif,
      smallWidth: 240,
      largeWidth: 427,
    },
    webp: {
      small: goldOpenHeart360Webp,
      large: goldOpenHeart640Webp,
      smallWidth: 240,
      largeWidth: 427,
    },
  },
  {
    name: 'Explorer tropic',
    parts: 'Explorer 39 · Open-heart dial · Tropic rubber',
    alt: 'Horatix watch with an Explorer 39 case, geometric open-heart dial, and black tropic rubber strap',
    width: 427,
    height: 640,
    avif: {
      small: explorerTropic360Avif,
      large: explorerTropic640Avif,
      smallWidth: 240,
      largeWidth: 427,
    },
    webp: {
      small: explorerTropic360Webp,
      large: explorerTropic640Webp,
      smallWidth: 240,
      largeWidth: 427,
    },
  },
]

function MaterialIcon({ name, className = '' }: { name: string; className?: string }) {
  return (
    <span className={`material-symbols-outlined ${className}`} aria-hidden="true">
      {name}
    </span>
  )
}

function WatchBuildCard({ build, primary = false }: { build: WatchBuild; primary?: boolean }) {
  const sizes = primary
    ? '(min-width: 1024px) 34vw, (min-width: 640px) 56vw, 82vw'
    : '(min-width: 1024px) 22vw, (min-width: 640px) 42vw, 82vw'

  return (
    <figure className={`home-watch-card${primary ? ' home-watch-card--primary' : ''}`}>
      <div className="home-watch-media">
        <picture>
          <source
            type="image/avif"
            srcSet={`${build.avif.small} ${build.avif.smallWidth}w, ${build.avif.large} ${build.avif.largeWidth}w`}
            sizes={sizes}
          />
          <source
            type="image/webp"
            srcSet={`${build.webp.small} ${build.webp.smallWidth}w, ${build.webp.large} ${build.webp.largeWidth}w`}
            sizes={sizes}
          />
          <img
            src={build.webp.large}
            alt={build.alt}
            width={build.width}
            height={build.height}
            loading={primary ? 'eager' : 'lazy'}
            fetchPriority={primary ? 'high' : 'auto'}
            decoding="async"
          />
        </picture>
      </div>
      <figcaption className="home-watch-caption">
        <strong>{build.name}</strong>
        <span>{build.parts}</span>
      </figcaption>
    </figure>
  )
}

export function HomePage() {
  // Scroll-reveal: fade + translate sections into view on intersection
  useEffect(() => {
    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))
    if (targets.length === 0) return

    targets.forEach((el) => {
      el.classList.add('transition-all', 'duration-1000', 'opacity-0', 'translate-y-10')
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0')
            entry.target.classList.remove('opacity-0', 'translate-y-10')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 },
    )

    targets.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return (
    <div className="overflow-x-hidden min-h-screen">
      <section className="home-hero">
        <div className="home-hero-pattern" aria-hidden="true" />
        <div className="home-hero-layout max-w-container-max mx-auto">
          <div className="home-hero-copy space-y-md text-center lg:text-left" data-reveal>
            <div className="inline-flex items-center gap-xs px-sm py-1 bg-surface-container rounded-full border border-outline-variant/30">
              <span className="w-2 h-2 rounded-full bg-[#a17f3c] animate-pulse glow-pip" />
              <span className="font-spec-label text-[10px] uppercase tracking-[0.2em] text-on-surface-variant">
                AI Studio
              </span>
            </div>
            <h1 className="font-display-lg text-display-lg md:text-[64px] leading-tight text-primary">
              Design a Watch Worth Wearing.
            </h1>
            <p className="font-body-lg text-body-lg text-secondary max-w-xl mx-auto lg:mx-0">
              Describe the watch you want. Horatix helps you pick compatible parts and refine the build as you go.
            </p>
            <div className="flex flex-col sm:flex-row gap-sm justify-center lg:justify-start pt-md">
              <Link
                to="/watch-builder"
                className="bg-primary text-on-primary px-lg py-md rounded-lg font-spec-label text-spec-label uppercase tracking-widest flex items-center justify-center gap-sm group"
              >
                <span>Launch AI Studio</span>
                <MaterialIcon name="arrow_forward" className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          <div className="home-build-showcase" data-reveal>
            <div className="home-build-halo" aria-hidden="true" />
            <ol className="home-build-gallery" aria-label="Watches designed with Horatix">
              {WATCH_BUILDS.map((build, index) => (
                <li key={build.name}>
                  <WatchBuildCard build={build} primary={index === 0} />
                </li>
              ))}
            </ol>
            <p className="home-gallery-hint">
              <span aria-hidden="true">←</span> Swipe to explore real Horatix builds <span aria-hidden="true">→</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
